import React, { useState, useRef, useEffect } from 'react';
import {
  Bell,
  Menu,
  Megaphone,
  Globe
} from 'lucide-react';
import { apiService } from '../services/api';
import NotificationCenter from './NotificationCenter';
import ThemeToggle from './ThemeToggle';
import UserMenu from './UserMenu';
import GlobalSearch from './GlobalSearch';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { useAnnouncementCount } from './AnnouncementPopup';

interface HeaderProps {
  onMenuToggle: () => void;
  onNavigate: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, onNavigate }) => {

  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const notificationRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { hasUpdate, currentVersion, latestVersion } = useVersionCheck();
  const unreadAnnouncementCount = useAnnouncementCount();

  const unreadNotifications = notifications.filter(n => !n.is_read && !n.is_dismissed);

  const handleOpenAnnouncements = () => {
    window.dispatchEvent(new Event('openAnnouncements'));
  };

  // Show simple browser notification (DISABLED)
  const showBrowserNotification = (title: string, body: string) => {
    // Browser notifications disabled - bildirimler sadece uygulama içinde gösterilecek
    return;
  };

  // Load notifications
  React.useEffect(() => {
    const loadNotifications = async () => {
      try {
        const prevCount = notifications.filter(n => !n.is_read && !n.is_dismissed).length;
        const response = await apiService.getNotifications();

        // Teknisyen ve calisan rolü admin/superadmin notification'larını görmesin
        const filteredResponse = response.filter((notification: any) => {
          if ((user?.role === 'teknisyen' || user?.role === 'calisan') && notification.created_by_user) {
            return !['admin', 'superadmin'].includes(notification.created_by_user.role);
          }
          return true;
        });

        const newCount = filteredResponse.filter((n: any) => !n.is_read && !n.is_dismissed).length;

        // Show browser notification if new notifications
        if (newCount > prevCount && prevCount >= 0) {
          showBrowserNotification(
            t('header.notifications'),
            t('header.notifications')
          );
        }

        setNotifications(filteredResponse);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };

    loadNotifications();

    // Auto-refresh notifications every 30 seconds (max 2 times per minute)
    const interval = setInterval(loadNotifications, 30000);

    // Listen for notification updates
    const handleNotificationUpdate = () => {
      loadNotifications();
    };

    window.addEventListener('notificationUpdate', handleNotificationUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationUpdate', handleNotificationUpdate);
    };
  }, [notifications.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800/80 h-16 sticky top-0 z-40 transition-all duration-200 shadow-sm shadow-slate-200/50 dark:shadow-slate-800/50">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left section */}
        <div className="flex items-center">
          <button
            onClick={onMenuToggle}
            className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
          <GlobalSearch onNavigate={onNavigate} />
        </div>

        {/* Right section */}
        <div className="flex items-center space-x-2">
          {/* System Status - Hidden on small screens */}
          <div className="hidden xl:flex items-center">
            {hasUpdate ? (
              <div className="flex items-center text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-full">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></div>
                <span className="hidden 2xl:inline">{t('header.updateAvailable')}</span>
                <span className="2xl:hidden">{t('header.update')}</span>
              </div>
            ) : (
              <div className="flex items-center text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="hidden 2xl:inline">{t('header.systemUpToDate')}</span>
                <span className="2xl:hidden">{t('header.upToDate')}</span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-1">
            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Language Switcher */}
            <div className="relative" ref={languageRef}>
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 relative transition-colors flex items-center gap-1"
                title={t('header.language')}
              >
                <Globe className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase hidden sm:inline">{language}</span>
              </button>

              {showLanguageMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-xl shadow-xl shadow-slate-200/60 dark:shadow-slate-900/60 border border-slate-200 dark:border-slate-800 z-50 overflow-hidden">
                  <button
                    onClick={() => { setLanguage('tr'); setShowLanguageMenu(false); }}
                    className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                      language === 'tr' ? 'font-semibold text-primary bg-primary/5' : 'text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="text-base">🇹🇷</span>
                    Türkçe
                  </button>
                  <button
                    onClick={() => { setLanguage('en'); setShowLanguageMenu(false); }}
                    className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                      language === 'en' ? 'font-semibold text-primary bg-primary/5' : 'text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="text-base">🇬🇧</span>
                    English
                  </button>
                </div>
              )}
            </div>

            {/* Announcements - For all authenticated users */}
            {user && (
              <button
                onClick={handleOpenAnnouncements}
                className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 relative transition-colors"
                title={t('header.announcements')}
              >
                <Megaphone className="w-5 h-5" />
                {unreadAnnouncementCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse font-medium">
                    {unreadAnnouncementCount > 9 ? '9+' : unreadAnnouncementCount}
                  </span>
                )}
              </button>
            )}

            {/* Notifications - Hide for calisan role */}
            {user?.role !== 'calisan' && (
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotificationCenter(!showNotificationCenter)}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 relative transition-colors"
                  title={t('header.notifications')}
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse font-medium">
                      {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Profile */}
            <UserMenu onNavigate={onNavigate} />
          </div>
        </div>
      </div>

      {/* Notification Center - Hide for calisan role */}
      {user?.role !== 'calisan' && (
        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={() => setShowNotificationCenter(false)}
        />
      )}
    </header>
  );
};

export default Header;
